/* lang-gloss.js — short learner's-dictionary translations of English words
 * into the page's language, for Timbrica's language tools (/text-to-ipa first).
 *
 * Data: /data/lang/gloss/{locale}/manifest.json names core.json (the ~6 000
 * most common words, loaded first — they are most of any text) and tail-N.json
 * (the rest, grouped by first letter, loaded only for the letters a text
 * needs). Built once by scripts/build-lang-gloss.php; read that header for why
 * this is a dictionary and not a translator called per word.
 *
 * A word that is not in the dictionary gets NO gloss rather than a guess: the
 * lookup tries the form as typed, then the British → American spelling, then
 * the base form (cats → cat, stopped → stop), and every candidate has to be a
 * real entry.
 *
 * Plain IIFE, no dependencies. Exposes window.LangGloss + module.exports.
 */
(function (root, factory) {
  'use strict';
  var G = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = G;
  if (typeof window !== 'undefined') window.LangGloss = G;
  else if (root) root.LangGloss = G;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var _base = '/data/lang/gloss';
  var _fetch = function (url) { return fetch(url, { credentials: 'same-origin' }); };
  var _locale = null;
  var _man = null;
  var _manP = null;
  var _dict = Object.create(null);     // word → [pos, text, pos?, text?]
  var _files = Object.create(null);    // file name → true | Promise

  function norm(w) {
    return String(w || '').toLowerCase().replace(/[’‘`´]/g, "'").replace(/[^a-z']/g, '');
  }

  /* Resolves to the manifest, or to null when this locale has no dictionary
   * (English, or a locale not built yet) — the caller then hides the feature.
   * A network failure also resolves null but is NOT remembered, so the next
   * call tries again. */
  function load(locale, opts) {
    if (opts && opts.base) _base = String(opts.base).replace(/\/$/, '');
    if (opts && opts.fetch) _fetch = opts.fetch;
    var loc = String(locale || '').toLowerCase().slice(0, 5);
    if (_manP && _locale === loc) return _manP;
    _locale = loc;
    _man = null;
    _dict = Object.create(null);
    _files = Object.create(null);
    var p = _fetch(_base + '/' + encodeURIComponent(loc) + '/manifest.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (m) {
        if (!m || !m.core || !m.letters) return null;
        _man = m;
        return file(m.core).then(function () { return m; });
      })
      .catch(function () { _man = null; if (_manP === p) _manP = null; return null; });
    _manP = p;
    return p;
  }

  function file(name) {
    var st = _files[name];
    if (st === true) return Promise.resolve();
    if (st) return st;
    var p = _fetch(_base + '/' + encodeURIComponent(_locale) + '/' + name + '?v=' + encodeURIComponent(_man.version))
      .then(function (r) {
        if (!r.ok) throw new Error('gloss ' + name + ' ' + r.status);
        return r.json();
      })
      .then(function (d) {
        for (var k in d) if (Object.prototype.hasOwnProperty.call(d, k)) _dict[k] = d[k];
        _files[name] = true;
      })
      .catch(function (e) { delete _files[name]; throw e; });
    _files[name] = p;
    return p;
  }

  /* Loads the tail groups the given words need. Words already answered by the
   * core cost nothing. Resolves even if a group fails (those words just stay
   * without a gloss); rejects only when there is no dictionary at all. */
  function ensure(words) {
    if (!_man) return Promise.resolve(false);
    var need = Object.create(null);
    for (var i = 0; i < words.length; i++) {
      var w = norm(words[i]);
      if (!w || _dict[w]) continue;
      var f = _man.letters[w.charAt(0)];
      if (f && _files[f] !== true) need[f] = 1;
    }
    var list = Object.keys(need);
    if (!list.length) return Promise.resolve(true);
    return Promise.all(list.map(function (f) { return file(f).catch(function () { return null; }); }))
      .then(function () { return true; });
  }

  /* British spellings the US-based list does not carry. Each rule runs only
   * when the word as typed is not an entry, and its result must be one — so
   * "four", "hour", "tour" never become "for", "hor", "tor". Pairs that are two
   * DIFFERENT words in the US list (tyre/tire, cheque/check, storey/story) are
   * deliberately absent: the rule would hand out the other word's meaning. */
  var UK_RULES = [
    [/our(s|ed|ing|ite|ites|able|ful|less|ism)?$/, 'or$1'],     // colour, favourite, rigour
    [/isation(s)?$/, 'ization$1'],                                // organisation
    [/is(e|es|ed|ing|er|ers)$/, 'iz$1'],                          // recognise, realised
    [/ys(e|es|ed|ing)$/, 'yz$1'],                                 // analyse
    [/tre(s)?$/, 'ter$1'],                                        // centre, theatre
    [/([^i])ence(s)?$/, '$1ense$2'],                              // offence, defence
    [/ogue(s)?$/, 'og$1'],                                        // catalogue, dialogue
    [/([aeiou])ll(ed|ing|er|ers)$/, '$1l$2'],                     // travelled, cancelling
    [/^sceptic/, 'skeptic'], [/^skilful/, 'skillful'], [/^fulfil(s|ment)?$/, 'fulfill$1'],
    [/^enrol(s|ment)?$/, 'enroll$1'], [/^instal(s|ment)?$/, 'install$1'],
    [/^ageing$/, 'aging'], [/^programme(s)?$/, 'program$1'], [/^grey/, 'gray'],
    [/^plough/, 'plow'], [/^mould/, 'mold'], [/^moustache/, 'mustache'],
    [/^pyjama/, 'pajama'], [/^aluminium$/, 'aluminum'], [/^jewellery$/, 'jewelry'],
  ];

  /* Base-form candidates, most likely first. Validated against the entries by
   * the caller, so over-generation is harmless. */
  function baseForms(w) {
    var c = [];
    var add = function (x) { if (x && x.length >= 2 && x !== w) c.push(x); };
    if (/ies$/.test(w)) add(w.slice(0, -3) + 'y');
    if (/ied$/.test(w)) add(w.slice(0, -3) + 'y');
    if (/(ier|iest)$/.test(w)) add(w.replace(/(ier|iest)$/, 'y'));
    if (/ves$/.test(w)) { add(w.slice(0, -3) + 'f'); add(w.slice(0, -3) + 'fe'); }
    if (/(sses|shes|ches|xes|zes|oes)$/.test(w)) add(w.slice(0, -2));
    if (/s$/.test(w) && !/(ss|us|is)$/.test(w)) add(w.slice(0, -1));
    if (/es$/.test(w)) add(w.slice(0, -2));
    if (/ing$/.test(w)) {
      var s = w.slice(0, -3);
      add(s); add(s + 'e');
      if (s.length > 2 && s.charAt(s.length - 1) === s.charAt(s.length - 2)) add(s.slice(0, -1));
    }
    if (/ed$/.test(w)) {
      var e = w.slice(0, -2);
      add(e); add(e + 'e'); add(w.slice(0, -1));
      if (e.length > 2 && e.charAt(e.length - 1) === e.charAt(e.length - 2)) add(e.slice(0, -1));
    }
    if (/(er|est)$/.test(w)) {
      var r = w.replace(/(er|est)$/, '');
      add(r); add(r + 'e');
      if (r.length > 2 && r.charAt(r.length - 1) === r.charAt(r.length - 2)) add(r.slice(0, -1));
    }
    if (/ly$/.test(w)) { add(w.slice(0, -2)); add(w.slice(0, -2) + 'le'); add(w.slice(0, -3) + 'y'); }
    if (/ness$/.test(w)) add(w.slice(0, -4));
    return c;
  }

  /* The entry for a word as typed, or via its possessive / British spelling /
   * base form. Returns {entry, via} or null. Synchronous: ensure() first. */
  function find(word) {
    var w = norm(word);
    if (!w) return null;
    if (_dict[w]) return { entry: _dict[w], via: 'exact', key: w };
    var stems = [w];
    if (/'s$/.test(w)) stems.push(w.slice(0, -2));
    else if (/s'$/.test(w)) stems.push(w.slice(0, -1));
    for (var i = 0; i < stems.length; i++) {
      var s = stems[i];
      if (i > 0 && _dict[s]) return { entry: _dict[s], via: 'exact', key: s };
      for (var u = 0; u < UK_RULES.length; u++) {
        if (!UK_RULES[u][0].test(s)) continue;
        var us = s.replace(UK_RULES[u][0], UK_RULES[u][1]);
        if (_dict[us]) return { entry: _dict[us], via: 'uk', key: us };
      }
      var bases = baseForms(s);
      for (var b = 0; b < bases.length; b++) {
        if (_dict[bases[b]]) return { entry: _dict[bases[b]], via: 'base', key: bases[b] };
        for (var v = 0; v < UK_RULES.length; v++) {
          if (!UK_RULES[v][0].test(bases[b])) continue;
          var ub = bases[b].replace(UK_RULES[v][0], UK_RULES[v][1]);
          if (_dict[ub]) return { entry: _dict[ub], via: 'base', key: ub };
        }
      }
    }
    return null;
  }

  /* hint: 'noun' | 'verb' | null — from the words around it (see posHint
   * below). A VERB hint (a modal or a subject pronoun just before: "would
   * like", "they cry") takes the verb reading over whatever comes first — the
   * first reading of like is the preposition «как», and "I would like a cup"
   * read «я бы как чашка» until the screenshot showed it. A NOUN hint (an
   * article or a preposition before) only replaces a VERB reading: an adjective
   * keeps its place ("the quiet room" stays «тихий», not «тишина»). */
  function lookup(word, hint) {
    var f = find(word);
    if (!f) return null;
    var e = f.entry;
    var senses = [];
    for (var i = 0; i + 1 < e.length; i += 2) senses.push({ pos: e[i], text: e[i + 1] });
    if (!senses.length) return null;
    var pick = 0;
    if (senses.length > 1 && hint) {
      var want = hint === 'verb' ? 'v' : hint === 'noun' ? 'n' : null;
      var swap = hint === 'verb' ? senses[0].pos !== 'v' : senses[0].pos === 'v';
      if (want && swap) {
        for (var k = 1; k < senses.length; k++) if (senses[k].pos === want) { pick = k; break; }
      }
    }
    return {
      text: senses[pick].text,
      short: senses[pick].text.split(', ')[0],
      pos: senses[pick].pos,
      senses: senses,
      picked: pick,
      via: f.via,
      key: f.key,
    };
  }

  /* All senses as one line: «плакать, кричать; плач, крик». */
  function full(g) {
    if (!g) return '';
    var ordered = [g.senses[g.picked]].concat(g.senses.filter(function (_, i) { return i !== g.picked; }));
    return ordered.map(function (s) { return s.text; }).join('; ');
  }

  var DET_NARROW = set('a an the my your his her its our their this that these those me him us them it');
  function set(s) { var o = Object.create(null); s.split(' ').forEach(function (w) { o[w] = 1; }); return o; }

  /* Noun-or-verb hint from the neighbours. Delegates to LangStress.resolvePOS
   * (the heteronym engine: "a RECord" vs "to reCORD") when it is on the page,
   * with ONE narrowing: before a heteronym, "to" means a verb, but in running
   * text "go to school" / "a trip to London" put a NOUN after it — so here
   * "to" counts only when an object follows ("to book a table"). */
  function posHint(prev, next) {
    var LS = (typeof window !== 'undefined' && window.LangStress)
      || (typeof self !== 'undefined' && self.LangStress)
      || (typeof globalThis !== 'undefined' && globalThis.LangStress) || null;
    if (!LS || !LS.resolvePOS) return null;
    var p = norm(prev);
    if (p === 'to') return (next && DET_NARROW[norm(next)]) ? 'verb' : null;
    var r = LS.resolvePOS(prev, next);
    return r && r.confident ? r.role : null;
  }

  /* ── Reverse: a word in the reader's language → the English words ──
   * Data: /data/lang/gloss-rev/{locale}/ — inverted from the forward files by
   * scripts/build-lang-gloss-reverse.php, candidates most frequent first
   * (школа → school; мир → world, peace). revKey() must stay the twin of the
   * PHP one there, or a typed word misses its shard. */
  var _revBase = '/data/lang/gloss-rev';
  var _rev = Object.create(null);      // locale → {man, map, files, p}

  function revKey(s, locale) {
    s = String(s || '').trim().toLowerCase().replace(/ё/g, 'е').replace(/[’`]/g, "'");
    if (locale === 'ar') {
      s = s.replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي')
        .replace(/[\u064B-\u0652\u0640]/g, '');
    }
    return s.replace(/\s+/g, ' ');
  }
  function bareKey(s) {
    try { return s.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (e) { return s; }
  }
  function revShard(key, locale) {
    var cp = key.codePointAt(0);
    if (cp == null) return 'x';
    if (locale === 'ko' && cp >= 0xAC00 && cp <= 0xD7A3) return 'k' + Math.floor((cp - 0xAC00) / 588);
    var h = cp.toString(16);
    while (h.length < 4) h = '0' + h;
    return h;
  }
  function revState(locale) {
    var loc = String(locale || '').toLowerCase().slice(0, 5);
    if (!_rev[loc]) _rev[loc] = { man: null, p: null, map: Object.create(null), files: Object.create(null) };
    return _rev[loc];
  }
  function loadReverse(locale) {
    var st = revState(locale);
    if (st.p) return st.p;
    var loc = String(locale || '').toLowerCase().slice(0, 5);
    st.p = _fetch(_revBase + '/' + encodeURIComponent(loc) + '/manifest.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (m) { st.man = (m && m.shards) ? m : null; return st.man; })
      .catch(function () { st.p = null; return null; });
    return st.p;
  }
  /* Resolves {typed word → [English candidates]} for the words that have any. */
  function reverse(words, locale) {
    return loadReverse(locale).then(function (man) {
      if (!man) return {};
      var st = revState(locale), need = Object.create(null), keys = [];
      for (var i = 0; i < words.length; i++) {
        var k = revKey(words[i], locale);
        if (!k) continue;
        keys.push([words[i], k]);
        var sh = man.shards[revShard(k, locale)];
        if (sh && st.files[sh] !== true) need[sh] = 1;
      }
      return Promise.all(Object.keys(need).map(function (f) {
        if (st.files[f] && st.files[f] !== true) return st.files[f];
        var p = _fetch(_revBase + '/' + encodeURIComponent(String(locale).toLowerCase()) + '/' + f + '?v=' + encodeURIComponent(man.version))
          .then(function (r) { if (!r.ok) throw new Error('gloss-rev ' + f); return r.json(); })
          .then(function (d) { for (var x in d) st.map[x] = d[x]; st.files[f] = true; })
          .catch(function () { delete st.files[f]; });
        st.files[f] = p;
        return p;
      })).then(function () {
        var out = {};
        for (var j = 0; j < keys.length; j++) {
          var hit = st.map[keys[j][1]] || st.map[bareKey(keys[j][1])];
          if (hit && hit.length) out[keys[j][0]] = hit.slice();
        }
        return out;
      });
    });
  }

  return {
    load: load,
    ensure: ensure,
    lookup: lookup,
    full: full,
    posHint: posHint,
    reverse: reverse,
    loadReverse: loadReverse,
    revKey: revKey,
    revShard: revShard,
    // exposed for unit tests
    find: find,
    baseForms: baseForms,
    norm: norm,
    _setDict: function (d, man) { _dict = d; _man = man || { letters: {}, version: 't' }; _locale = 'xx'; },
    get ready() { return !!_man; },
    get manifest() { return _man; },
  };
});
